import React from 'react';
import { BreadcrumbGroup, BreadcrumbGroupProps } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSpaWrapper } from './SpaLink';

interface SiteBreadcrumbsProps {
  overrides: Record<string, string>;
  /**
   * Optional navigation guard set by the current page (via outlet context).
   * Called before navigating — returns true to allow, false to block.
   * Used by pages with unsaved changes to show a confirmation modal
   * before the user leaves (Cloudscape unsaved-changes pattern).
   */
  navigationGuard: ((href: string) => boolean) | null;
}

/**
 * Centralized breadcrumb component for the navigation redesign.
 * Derives breadcrumb trail from the current URL path.
 * Dynamic segments (event name, shift name) are passed via the `overrides` prop
 * which pages set through useOutletContext.
 */
export function SiteBreadcrumbs({ overrides, navigationGuard }: SiteBreadcrumbsProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const spaWrapper = useSpaWrapper();

  const items = getBreadcrumbsForPath(location.pathname, overrides, t);

  // Home page has no breadcrumbs
  if (items.length === 0) return null;

  return (
    // Wrapper carries a stable data-testid: Cloudscape BreadcrumbGroup exposes no per-element
    // testid hook, so integration tests scope to this container instead of the localized
    // nav[aria-label="Breadcrumbs"] selector (see RedVelvetReactWebsiteTests AGENTS.md).
    <div data-testid="breadcrumbs">
      <BreadcrumbGroup
        items={items}
        ariaLabel={t('breadcrumbs.ariaLabel')}
        onFollow={(event) => {
          event.preventDefault();
          const href = event.detail.href;
          // Cloudscape unsaved-changes pattern: check guard before navigating.
          // If the guard returns false, the page will show its own confirmation modal.
          if (navigationGuard && !navigationGuard(href)) return;
          // Navigate via the SPA wrapper so persistent URL params (features, timezone) are carried
          // over to the target — a bare navigate(href) dropped them (CR-291797692 rev 7).
          spaWrapper({ url: href })(event);
        }}
      />
    </div>
  );
}

/**
 * Map URL path to breadcrumb trail. Uses an explicit switch rather than dynamic path-segment
 * parsing because breadcrumb labels don't always match URL segments (e.g., /create-event shows
 * "Coordinator Activity" as a parent breadcrumb even though it's not in the URL path).
 */
function getBreadcrumbsForPath(
  pathname: string,
  overrides: Record<string, string>,
  t: (key: string) => string
): BreadcrumbGroupProps.Item[] {
  const root: BreadcrumbGroupProps.Item = {
    text: t('breadcrumbs.home'),
    href: '/',
  };

  // Shared parent breadcrumb for all coordinator sub-pages (create/edit event,
  // track attendance, manage signups/waitlist) — defined once to avoid duplication.
  const coordinatorActivity: BreadcrumbGroupProps.Item = {
    text: t('breadcrumbs.coordinatorActivity'),
    href: '/coordinator-activity',
  };

  // Normalize trailing slash
  const normalizedPath =
    pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;

  switch (normalizedPath) {
    case '/':
      // Home page — no breadcrumbs
      return [];

    case '/view-event':
      return [root, { text: overrides.eventName || '...', href: '#' }];

    case '/my-activity':
      return [root, { text: t('breadcrumbs.volunteerActivity'), href: '/my-activity' }];

    case '/coordinator-activity':
      return [root, coordinatorActivity];

    case '/create-event':
      return [
        root,
        coordinatorActivity,
        { text: t('breadcrumbs.createEvent'), href: '/create-event' },
      ];

    case '/edit-event':
      return [root, coordinatorActivity, { text: t('breadcrumbs.editEvent'), href: '/edit-event' }];

    case '/track-attendance':
      return [
        root,
        coordinatorActivity,
        { text: t('breadcrumbs.attendanceSheet'), href: '/track-attendance' },
      ];

    case '/manage-signups':
      return [
        root,
        coordinatorActivity,
        { text: t('breadcrumbs.manageSignups'), href: '/manage-signups' },
        ...(overrides.shiftName ? [{ text: overrides.shiftName, href: '#' }] : []),
      ];

    case '/manage-waitlist':
      return [
        root,
        coordinatorActivity,
        { text: t('breadcrumbs.manageWaitlist'), href: '/manage-waitlist' },
      ];

    case '/ambassadors':
      return [root, { text: t('breadcrumbs.ambassadors'), href: '/ambassadors' }];

    case '/ambassadors/register':
      return [
        root,
        { text: t('breadcrumbs.ambassadors'), href: '/ambassadors' },
        { text: t('breadcrumbs.ambassadorsRegister'), href: '/ambassadors/register' },
      ];

    case '/ambassadors/offboarding':
      return [
        root,
        { text: t('breadcrumbs.ambassadors'), href: '/ambassadors' },
        { text: t('breadcrumbs.ambassadorsOffboarding'), href: '/ambassadors/offboarding' },
      ];

    case '/ambassadors/bulk-update':
      return [
        root,
        { text: t('breadcrumbs.ambassadors'), href: '/ambassadors' },
        { text: t('breadcrumbs.ambassadorsBulkUpdate'), href: '/ambassadors/bulk-update' },
      ];

    default:
      // Unknown route — just show root
      return [root];
  }
}
